import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dumbbell, Activity, Zap, Stethoscope } from "lucide-react";

const META = {
  strength:          { label: "Strength",        Icon: Dumbbell,    color: "#F59E0B" },
  conditioning:      { label: "Conditioning",    Icon: Activity,    color: "#22D3EE" },
  speed_agility:     { label: "Speed / Agility", Icon: Zap,         color: "#A78BFA" },
  athletic_training: { label: "AT Rehab",        Icon: Stethoscope, color: "#34D399" },
};

function fmtDay(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function WorkloadTypeDetailDialog({ open, onOpenChange, sessionType, days = 14 }) {
  const [data, setData] = useState(null);
  const m = META[sessionType] || {};
  const Icon = m.Icon;

  useEffect(() => {
    if (!open || !sessionType) return;
    setData(null);
    (async () => {
      try {
        const r = await api.get(`/workload/team-type-detail?session_type=${sessionType}&days=${days}`);
        setData(r.data);
      } catch (e) {
        setData({ series: [], player_totals: [], total_minutes: 0, total_sessions: 0 });
      }
    })();
  }, [open, sessionType, days]);

  const chartData = (data?.series || []).map((s) => ({
    day: fmtDay(s.date),
    rawDate: s.date,
    minutes: s.minutes,
    sessions: s.sessions,
    players: s.players,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="workload-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {Icon && (
              <span className="w-7 h-7 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${m.color}22`, color: m.color }}>
                <Icon className="h-4 w-4" />
              </span>
            )}
            {m.label || sessionType} — {days}-day breakdown
          </DialogTitle>
          <DialogDescription>
            {data
              ? `${data.total_minutes} verified minutes across ${data.total_sessions} sessions`
              : "Loading..."}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 h-56 min-h-[220px] w-full" data-testid="workload-detail-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="day" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} />
              <Tooltip
                contentStyle={{
                  background: "#0b0f17",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="rounded-md border border-white/10 bg-[#0b0f17] p-3 text-xs">
                      <div className="font-semibold mb-1">{p.day}</div>
                      <div className="text-white/70">{p.minutes} min · {p.sessions} session{p.sessions === 1 ? "" : "s"}</div>
                      {p.players?.length > 0 && (
                        <div className="mt-2 space-y-0.5 text-white/60">
                          {p.players.slice(0, 6).map((pl) => (
                            <div key={pl.player_user_id}>· {pl.player_name} <span className="text-white/40">({pl.minutes}m)</span></div>
                          ))}
                          {p.players.length > 6 && <div className="text-white/40">+ {p.players.length - 6} more</div>}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
              <Bar dataKey="minutes" fill={m.color || "#22D3EE"} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {/* Per-day list */}
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">
              Days with activity
            </div>
            <div className="space-y-1 text-sm max-h-56 overflow-auto pr-1">
              {chartData.filter((d) => d.minutes > 0).length === 0 && (
                <div className="text-white/40 text-xs">No verified sessions in this window.</div>
              )}
              {chartData.filter((d) => d.minutes > 0).map((d) => (
                <div key={d.rawDate} className="rounded border border-white/5 bg-white/[0.02] p-2"
                     data-testid={`detail-day-${d.rawDate}`}>
                  <div className="flex items-center justify-between font-mono-num">
                    <span className="text-white/70">{d.day}</span>
                    <span className="text-white"><b>{d.minutes}</b><span className="text-white/40 ml-1 text-xs">min · {d.sessions}</span></span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {d.players?.slice(0, 8).map((p) => (
                      <Link
                        key={p.player_user_id}
                        to={`/dashboard/player/${p.player_user_id}`}
                        className="text-[11px] rounded px-1.5 py-0.5 border border-white/10 bg-white/5 hover:bg-white/10 text-white/70"
                        title={`${p.minutes} min · ${p.sessions} session${p.sessions === 1 ? "" : "s"}`}
                      >
                        {p.player_name} <span className="text-white/40">{p.minutes}m</span>
                      </Link>
                    ))}
                    {d.players?.length > 8 && (
                      <span className="text-[11px] text-white/40 px-1">+{d.players.length - 8}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Player totals */}
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">
              Player totals
            </div>
            <div className="space-y-1 text-sm max-h-56 overflow-auto pr-1">
              {(data?.player_totals || []).length === 0 && (
                <div className="text-white/40 text-xs">—</div>
              )}
              {(data?.player_totals || []).map((p) => (
                <Link
                  key={p.player_user_id}
                  to={`/dashboard/player/${p.player_user_id}`}
                  className="flex items-center justify-between rounded border border-white/5 bg-white/[0.02] p-2 hover:bg-white/[0.05]"
                  data-testid={`detail-player-${p.player_user_id}`}
                >
                  <span className="text-white/80 truncate">{p.player_name}</span>
                  <span className="font-mono-num text-white/70 text-xs">{p.minutes}m · {p.sessions}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
